import { divIcon } from 'leaflet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';
import { Link, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { useRealtime } from '../../providers/RealtimeProvider';
import { listActives } from '../../services/activeService';
import {
  assignBooking,
  changeStatus,
  getBooking,
  updateBooking,
} from '../../services/bookingService';
import { getCompanyProfile } from '../../services/companyService';
import { getFare } from '../../services/fareService';
import { listVehicles } from '../../services/vehicleService';
import { getMapboxTileLayer } from '../../utils/mapbox';
import ResolvedAddress from '../../components/ResolvedAddress';
import { buildModernReceiptPdf, buildTripFromBooking } from '../../utils/receiptPdf';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

const ACTIVE_STATUSES = new Set(['Assigned', 'EnRoute', 'PickedUp']);

function isDestinationLegActive(booking) {
  const tripSession = booking?.tripSession || {};
  return Boolean(
    booking?.status === 'PickedUp' ||
    booking?.pickedUpAt ||
    tripSession.startedAt ||
    tripSession.sessionId ||
    tripSession.clientSessionId ||
    tripSession.lastHeartbeatAt ||
    tripSession.lastMeter ||
    tripSession.lastKnownLocalState,
  );
}

function getBookingIdentity(booking) {
  return booking?.id || booking?._id;
}

function formatRelativeTime(date) {
  if (!date) return '';
  const diff = Math.round((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const mins = Math.round(diff / 60);
  return mins < 60 ? `${mins} min ago` : `${Math.round(mins / 60)} hr ago`;
}

function distanceMeters(left, right) {
  if (!left || !right) return Infinity;
  const toRad = (value) => value * Math.PI / 180;
  const dLat = toRad(right.lat - left.lat);
  const dLng = toRad(right.lng - left.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(left.lat)) * Math.cos(toRad(right.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function MapAutoFit({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length < 2) return;
    map.fitBounds(positions, { padding: [44, 44], maxZoom: 15 });
  }, [map, positions]);
  return null;
}

function makeDriverIcon(heading) {
  const hasHeading = Number.isFinite(heading);
  return divIcon({
    className: '',
    html: `<div class="tracking-driver-marker">
      <div class="tracking-driver-marker__pulse"></div>
      ${hasHeading ? `<div class="tracking-driver-marker__arrow" style="transform:translateX(-50%) rotate(${heading}deg)"></div>` : ''}
      <div class="tracking-driver-marker__core"></div>
    </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });
}

function SmoothBookingMarker({ position, icon }) {
  const markerRef = useRef(null);
  const previous = useRef(position);
  useEffect(() => {
    const marker = markerRef.current;
    const from = previous.current;
    previous.current = position;
    if (!marker || !from || !position) return undefined;
    const started = performance.now();
    let frame;
    const animate = (now) => {
      const progress = Math.min(1, (now - started) / 900);
      marker.setLatLng([from[0] + ((position[0] - from[0]) * progress), from[1] + ((position[1] - from[1]) * progress)]);
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [position]);
  return <Marker ref={markerRef} position={position} icon={icon} />;
}


function getBookingFareValue(booking) {
  if (!booking) return null;
  const candidates = booking.status === 'Completed'
    ? [booking.finalFare, booking.flatRateAmount, booking.estimatedFare, booking.fare]
    : [booking.estimatedFare, booking.flatRateAmount, booking.finalFare, booking.fare];

  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

const pickupIcon = divIcon({
  className: '',
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
    <path d="M15 0C6.716 0 0 6.716 0 15c0 9.941 15 25 15 25S30 24.941 30 15C30 6.716 23.284 0 15 0z" fill="#22c55e" stroke="white" stroke-width="2"/>
    <circle cx="15" cy="15" r="7" fill="white" opacity="0.92"/>
    <text x="15" y="19.5" text-anchor="middle" fill="#15803d" font-size="11" font-family="Arial,sans-serif" font-weight="bold">P</text>
  </svg>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -42],
});

const dropoffIcon = divIcon({
  className: '',
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
    <path d="M15 0C6.716 0 0 6.716 0 15c0 9.941 15 25 15 25S30 24.941 30 15C30 6.716 23.284 0 15 0z" fill="#ef4444" stroke="white" stroke-width="2"/>
    <circle cx="15" cy="15" r="7" fill="white" opacity="0.92"/>
    <text x="15" y="19.5" text-anchor="middle" fill="#b91c1c" font-size="11" font-family="Arial,sans-serif" font-weight="bold">D</text>
  </svg>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -42],
});

const statusOptions = [
  { value: 'Pending', label: 'Scheduled' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'EnRoute', label: 'En route' },
  { value: 'PickedUp', label: 'Picked up' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'NoShow', label: 'No show' },
];

const BookingDetail = () => {
  const { id } = useParams();
  const { socket } = useRealtime();

  const [booking, setBooking] = useState(null);
  const [form, setForm] = useState({
    customerName: '',
    phoneNumber: '',
    pickupAddress: '',
    dropoffAddress: '',
    pickupTime: '',
    notes: '',
    fare: '',
    fareStrategy: 'meter',
    flatRateName: '',
    flatRateAmount: '',
  });
  const [assignmentMode, setAssignmentMode] = useState('manual');
  const [assignment, setAssignment] = useState({ driverId: '', cabNumber: '' });
  const [statusForm, setStatusForm] = useState({ toStatus: '', reason: '', fee: '' });
  const [vehicles, setVehicles] = useState([]);
  const [actives, setActives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printingReceipt, setPrintingReceipt] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Live tracking
  const [driverPosition, setDriverPosition] = useState(null);   // { lat, lng, heading? }
  const [driverTrail, setDriverTrail] = useState([]);
  const [routeCoords, setRouteCoords] = useState(null);          // [[lat,lng], ...]
  const [etaMinutes, setEtaMinutes] = useState(null);
  const [etaUpdatedAt, setEtaUpdatedAt] = useState(null);
  const lastLocationAtRef = useRef(null);
  const lastEtaRequestRef = useRef({ at: 0, position: null, target: null });
  const mapboxTiles = useMemo(() => getMapboxTileLayer(), []);
  const currentBookingId = getBookingIdentity(booking);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [bookingRes, vehiclesRes, activesRes] = await Promise.all([
          getBooking(id),
          listVehicles(),
          listActives(),
        ]);
        const bookingData = bookingRes.data?.booking || bookingRes.data?.data || bookingRes.data;
        const vehicleList =
          vehiclesRes.data?.vehicles || vehiclesRes.data?.results || vehiclesRes.data || [];
        const activeList =
          activesRes.data?.data || activesRes.data?.actives || activesRes.data || [];
        const normalizedActives = Array.isArray(activeList) ? activeList : [];

        setBooking(bookingData);
        setActives(normalizedActives);

        // Seed initial driver position from persisted driverLocation
        const initLoc = bookingData?.driverLocation;
        if (Array.isArray(initLoc?.coordinates) && initLoc.coordinates.length === 2) {
          const [lng, lat] = initLoc.coordinates;
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setDriverPosition({ lat, lng, heading: initLoc.heading ?? null });
            setDriverTrail([[lat, lng]]);
            lastLocationAtRef.current = new Date();
          }
        }

        const rawDriverId =
          bookingData?.driverId ??
          bookingData?.driver?._id ??
          bookingData?.driver?._id?.toString?.();
        const initialDriverId = rawDriverId ? String(rawDriverId) : '';
        let initialCabNumber = bookingData?.cabNumber ?? bookingData?.assignedCab ?? '';
        if (initialCabNumber !== null && initialCabNumber !== undefined && initialCabNumber !== '') {
          initialCabNumber = String(initialCabNumber);
        } else {
          initialCabNumber = '';
        }
        const activeMatch = normalizedActives.find((active) => {
          const candidate = active?.driverId ?? active?._id;
          return candidate != null && String(candidate) === initialDriverId;
        });
        if (!initialCabNumber && activeMatch?.cabNumber) {
          initialCabNumber = String(activeMatch.cabNumber);
        }
        setForm({
          customerName: bookingData?.customerName || '',
          phoneNumber: bookingData?.phoneNumber || '',
          pickupAddress: bookingData?.pickupAddress || '',
          dropoffAddress: bookingData?.dropoffAddress || '',
          pickupTime: bookingData?.pickupTime ? bookingData.pickupTime.substring(0, 16) : '',
          notes: bookingData?.notes || '',
          fare: (getBookingFareValue(bookingData) ?? '').toString(),
          fareStrategy: bookingData?.fareStrategy === 'flat' ? 'flat' : 'meter',
          flatRateName: bookingData?.flatRateName || '',
          flatRateAmount: bookingData?.flatRateAmount != null ? String(bookingData.flatRateAmount) : '',
        });
        setAssignment({
          driverId: initialDriverId,
          cabNumber: initialCabNumber,
        });
        setStatusForm({ toStatus: bookingData?.status || 'Pending', reason: '', fee: '' });
        setVehicles(Array.isArray(vehicleList) ? vehicleList : []);
      } catch (err) {
        const msg = err.response?.data?.message || 'Unable to load booking';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ── ETA / live tracking ────────────────────────────────────────
  const calculateETA = useCallback(async (fromCoords, toCoords) => {
    if (!MAPBOX_TOKEN || !fromCoords || !toCoords) return;
    try {
      const url =
        `https://api.mapbox.com/directions/v5/mapbox/driving/` +
        `${fromCoords[0]},${fromCoords[1]};${toCoords[0]},${toCoords[1]}` +
        `?overview=full&geometries=geojson&access_token=${MAPBOX_TOKEN}`;
      const res = await fetch(url);
      const data = await res.json();
      const route = data?.routes?.[0];
      if (!route) return;
      setEtaMinutes(Math.max(1, Math.round(route.duration / 60)));
      setEtaUpdatedAt(new Date());
      const geom = route.geometry?.coordinates;
      if (Array.isArray(geom) && geom.length >= 2) {
        setRouteCoords(geom.map(([lng, lat]) => [lat, lng]));
      }
    } catch (_e) {
      // ETA is best-effort
    }
  }, []);

  // Socket: driver:location → update position + recalculate ETA
  useEffect(() => {
    if (!socket || !currentBookingId) return;
    const handler = ({ booking: evtBooking, bookingId: evtBookingId, driverId: evtDriverId, driverLocation }) => {
      const evtId = getBookingIdentity(evtBooking) || evtBookingId;
      if (evtId && evtId !== currentBookingId) return;
      if (!evtId && evtDriverId && booking?.driverId && String(evtDriverId) !== String(booking.driverId)) return;
      if (evtBooking?.status || evtBooking?.tripSession) {
        setBooking((prev) => (prev ? { ...prev, ...evtBooking } : evtBooking));
      }
      if (!Array.isArray(driverLocation?.coordinates) || driverLocation.coordinates.length !== 2) return;
      const [lng, lat] = driverLocation.coordinates;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const pos = { lat, lng, heading: driverLocation.heading ?? null, speed: driverLocation.speed ?? driverLocation.speedMps ?? null, accuracy: driverLocation.accuracy ?? null, motionState: driverLocation.motionState, trackingHealth: driverLocation.trackingHealth, capturedAt: driverLocation.deviceCapturedAt || driverLocation.updatedAt };
      setDriverPosition(pos);
      setDriverTrail((current) => [...current, [lat, lng]].slice(-30));
      lastLocationAtRef.current = new Date();
    };
    socket.on('driver:location', handler);
    return () => socket.off('driver:location', handler);
  }, [socket, currentBookingId, booking?.driverId]);

  // Socket: assignment:updated → apply fresh status/fields (e.g. driver marks "Picked up")
  useEffect(() => {
    if (!socket || !currentBookingId) return;
    const handler = ({ booking: evtBooking }) => {
      const evtId = getBookingIdentity(evtBooking);
      if (!evtId || evtId !== currentBookingId) return;
      setBooking((prev) => (prev ? { ...prev, ...evtBooking } : evtBooking));
    };
    socket.on('assignment:updated', handler);
    return () => socket.off('assignment:updated', handler);
  }, [socket, currentBookingId]);

  // Recalculate ETA whenever driver position changes
  useEffect(() => {
    if (!driverPosition || !booking) return;
    if (!ACTIVE_STATUSES.has(booking.status)) return;
    const destinationLegActive = isDestinationLegActive(booking);
    const targetLng = destinationLegActive ? booking.dropoffLon : booking.pickupLon;
    const targetLat = destinationLegActive ? booking.dropoffLat : booking.pickupLat;
    if (!Number.isFinite(Number(targetLat)) || !Number.isFinite(Number(targetLng))) return;
    const target = { lat: Number(targetLat), lng: Number(targetLng) };
    const previous = lastEtaRequestRef.current;
    const targetChanged = !previous.target || distanceMeters(previous.target, target) > 25;
    const movedMeaningfully = !previous.position || distanceMeters(previous.position, driverPosition) >= 100;
    if (!targetChanged && !movedMeaningfully && Date.now() - previous.at < 30_000) return;
    lastEtaRequestRef.current = { at: Date.now(), position: { lat: driverPosition.lat, lng: driverPosition.lng }, target };
    calculateETA(
      [driverPosition.lng, driverPosition.lat],
      [Number(targetLng), Number(targetLat)],
    );
  }, [driverPosition, booking?.status, booking?.pickupLon, booking?.pickupLat, booking?.dropoffLon, booking?.dropoffLat, calculateETA, booking]);

  // Polling fallback: re-fetch booking every 30 s if no recent socket event
  useEffect(() => {
    if (!currentBookingId || !ACTIVE_STATUSES.has(booking?.status)) return;
    const interval = setInterval(async () => {
      const lastAt = lastLocationAtRef.current;
      if (lastAt && Date.now() - new Date(lastAt).getTime() < 28000) return;
      try {
        const res = await getBooking(id);
        const data = res.data?.booking || res.data?.data || res.data;
        if (data?.status) {
          setBooking((prev) => (prev ? { ...prev, ...data } : data));
        }
        const loc = data?.driverLocation;
        if (!Array.isArray(loc?.coordinates) || loc.coordinates.length !== 2) return;
        const [lng, lat] = loc.coordinates;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        setDriverPosition({ lat, lng, heading: loc.heading ?? null });
        setDriverTrail((current) => [...current, [lat, lng]].slice(-30));
        lastLocationAtRef.current = new Date();
      } catch (_e) { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [currentBookingId, booking?.status, id]);

  const driverOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    actives.forEach((active) => {
      const value = active?.driverId ?? active?._id;
      const normalizedValue = value != null ? String(value) : '';
      if (!normalizedValue || seen.has(normalizedValue)) return;
      seen.add(normalizedValue);
      const name = `${active?.firstName || ''} ${active?.lastName || ''}`.trim() || normalizedValue || 'Active driver';
      const detailParts = [];
      if (active?.cabNumber) detailParts.push(`Cab ${active.cabNumber}`);
      if (active?.availability) detailParts.push(active.availability);
      const label = detailParts.length ? `${name} - ${detailParts.join(' - ')}` : name;
      options.push({ value: normalizedValue, label });
    });

    const currentDriverId =
      assignment.driverId || (booking?.driverId != null ? String(booking.driverId) : '');
    if (currentDriverId && !seen.has(currentDriverId)) {
      const fallbackLabel = booking?.driverName || currentDriverId;
      options.push({ value: currentDriverId, label: `${fallbackLabel} (inactive)` });
    }

    return options;
  }, [actives, assignment.driverId, booking?.driverId, booking?.driverName]);

  const vehicleOptions = useMemo(() => {
    const map = new Map();

    vehicles.forEach((vehicle) => {
      const rawValue = vehicle?.cabNumber ?? vehicle?._id;
      const value = rawValue != null ? String(rawValue) : '';
      if (!value) return;
      const label = `Cab ${vehicle?.cabNumber || vehicle?._id?.slice?.(-4) || ''}`.trim();
      map.set(value, label);
    });

    actives.forEach((active) => {
      if (!active?.cabNumber) return;
      const value = String(active.cabNumber);
      if (map.has(value)) return;
      const descriptors = [active?.make, active?.model, active?.color].filter(Boolean).join(' ');
      const label = descriptors
        ? `Cab ${active.cabNumber} (${descriptors})`
        : `Cab ${active.cabNumber}`;
      map.set(value, label);
    });

    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [vehicles, actives]);

  const selectedActive = useMemo(
    () => {
      const normalized = assignment.driverId ? String(assignment.driverId) : '';
      if (!normalized) return undefined;
      return actives.find((active) => {
        const candidate = active?.driverId ?? active?._id;
        return candidate != null && String(candidate) === normalized;
      });
    },
    [actives, assignment.driverId],
  );

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssignmentChange = (event) => {
    const { name, value } = event.target;
    if (name === 'driverId') {
      setAssignment((prev) => {
        const next = { ...prev, [name]: value };
        const match = actives.find((active) => {
          const candidate = active?.driverId ?? active?._id;
          return candidate != null && String(candidate) === value;
        });
        if (match?.cabNumber) {
          next.cabNumber = String(match.cabNumber);
        }
        return next;
      });
    } else {
      setAssignment((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleStatusChange = (event) => {
    const { name, value } = event.target;
    setStatusForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitUpdate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const payload = {
        ...form,
        pickupTime: form.pickupTime ? new Date(form.pickupTime).toISOString() : undefined,
        fare: form.fare ? Number(form.fare) : undefined,
      };
      if (form.fareStrategy === 'flat') {
        const amount = Number(form.flatRateAmount || form.fare);
        if (!Number.isFinite(amount) || amount < 0) {
          setError('Enter a valid fixed fare.');
          setSaving(false);
          return;
        }
        payload.fareStrategy = 'flat';
        payload.flatRateName = form.flatRateName?.trim() || 'Manual fixed fare';
        payload.flatRateAmount = amount;
        payload.estimatedFare = amount;
      } else {
        payload.fareStrategy = 'meter';
        payload.flatRateName = undefined;
        payload.flatRateAmount = undefined;
      }
      const res = await updateBooking(id, payload);
      const updated = res.data?.data || res.data?.booking || null;
      setBooking((prev) => ({
        ...prev,
        ...(updated || {}),
        ...(updated ? {} : payload),
      }));
      setMessage('Booking details updated');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update booking';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitAssignment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (assignmentMode === 'auto') {
        const res = await assignBooking(id, { dispatchMethod: 'auto' });
        const updated = res.data?.data || res.data?.booking || {};
        setBooking((prev) => ({ ...prev, ...updated, needs_reassignment: false }));
        setMessage('Assignment queued for auto dispatch');
      } else {
        const payload = {
          driverId: assignment.driverId,
          cabNumber: assignment.cabNumber,
          dispatchMethod: 'manual',
        };
        const res = await assignBooking(id, payload);
        const updated = res.data?.data || res.data?.booking || {};
        setBooking((prev) => ({ ...prev, ...updated, needs_reassignment: false }));
        setMessage('Booking assigned to driver');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to assign booking';
      if (err.response?.data?.needsManual) {
        setBooking((prev) => (prev ? { ...prev, needs_reassignment: true } : prev));
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitStatusChange = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const payload = {
        toStatus: statusForm.toStatus,
        reason: statusForm.reason || undefined,
        fee: statusForm.fee ? Number(statusForm.fee) : undefined,
      };
      const res = await changeStatus(id, payload);
      const updated = res.data?.data || res.data?.booking || {};
      setBooking((prev) => ({ ...prev, ...updated }));
      const statusLabel =
        statusOptions.find((option) => option.value === payload.toStatus)?.label || payload.toStatus;
      setMessage(
        payload.toStatus === 'Cancelled'
          ? 'Booking cancelled.'
          : `Booking marked ${statusLabel}.`,
      );
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change status';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePrintReceipt = async () => {
    if (!booking) return;
    setPrintingReceipt(true);
    try {
      const [companyRes, fareRes] = await Promise.all([
        getCompanyProfile(),
        getFare().catch(() => ({ data: null })),
      ]);
      const fetchedCompany = companyRes.data?.company || {};
      const companyInfo = {
        name: fetchedCompany.name || 'TaxiOps Transportation LLC',
        address: fetchedCompany.address || '',
        phone: fetchedCompany.phone || '',
        email: fetchedCompany.email || '',
      };
      const fareConfig = fareRes?.data?.fare || fareRes?.data || null;
      const trip = buildTripFromBooking(booking, fareConfig);
      await buildModernReceiptPdf(trip, companyInfo, MAPBOX_TOKEN);
    } catch (e) {
      // silent — PDF errors don't need to surface in the booking UI
    } finally {
      setPrintingReceipt(false);
    }
  };

  const actions = (
    <Link to="/bookings" className="btn btn-ghost">
      Back to list
    </Link>
  );

  const renderSummary = () => {
    if (!booking) return null;
    const statusLabel =
      statusOptions.find((option) => option.value === booking.status)?.label ||
      booking.status ||
      'Scheduled';

    const isActive = ACTIVE_STATUSES.has(booking.status);
    const destinationLegActive = isDestinationLegActive(booking);
    const displayFare = getBookingFareValue(booking);

    const pickupLatLng =
      Number.isFinite(Number(booking.pickupLat)) && Number.isFinite(Number(booking.pickupLon))
        ? [Number(booking.pickupLat), Number(booking.pickupLon)]
        : null;
    const dropoffLatLng =
      Number.isFinite(Number(booking.dropoffLat)) && Number.isFinite(Number(booking.dropoffLon))
        ? [Number(booking.dropoffLat), Number(booking.dropoffLon)]
        : null;
    const driverLatLng = driverPosition ? [driverPosition.lat, driverPosition.lng] : null;

    const fitPositions = [driverLatLng, destinationLegActive ? dropoffLatLng : pickupLatLng].filter(Boolean);

    const mapCenter =
      driverLatLng ||
      (destinationLegActive ? dropoffLatLng : pickupLatLng) ||
      pickupLatLng ||
      [28.29, -81.41];
    const tileUrl = mapboxTiles?.url || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const tileAttr = mapboxTiles?.attribution || '&copy; OpenStreetMap contributors';

    return (
      <div className="panel" style={{ marginBottom: '24px' }}>
        <div className="panel-header">
          <h3>Booking overview</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className={`badge ${booking.status === 'Completed' ? 'badge-success' : booking.status === 'Cancelled' ? 'badge-warning' : 'badge-info'}`}>
              {statusLabel}
            </span>
            {booking.status === 'Completed' && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                onClick={handlePrintReceipt}
                disabled={printingReceipt}
              >
                {printingReceipt ? 'Generating…' : 'Print receipt'}
              </button>
            )}
          </div>
        </div>
        <div className="panel-body">
          <dl className="meta-grid">
            <div>
              <dt>Customer</dt>
              <dd>{booking.customerName || '—'}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{booking.phoneNumber || '—'}</dd>
            </div>
            <div>
              <dt>Pickup</dt>
              <dd>{booking.pickupTime ? new Date(booking.pickupTime).toLocaleString() : '—'}</dd>
            </div>
            <div>
              <dt>Pickup address</dt>
              <dd><ResolvedAddress address={booking.pickupAddress} lat={booking.pickupLat} lon={booking.pickupLon} /></dd>
            </div>
            <div>
              <dt>Drop-off</dt>
              <dd><ResolvedAddress address={booking.dropoffAddress} lat={booking.dropoffLat} lon={booking.dropoffLon} /></dd>
            </div>
            <div>
              <dt>Driver</dt>
              <dd>{booking.driverName || booking.driverId || 'Unassigned'}</dd>
            </div>
            <div>
              <dt>Cab</dt>
              <dd>{booking.cabNumber || '—'}</dd>
            </div>
            <div>
              <dt>Fare</dt>
              <dd>{formatCurrency(displayFare)}</dd>
            </div>
            {isActive && etaMinutes !== null && (
              <div style={{ gridColumn: '1 / -1' }}>
                <dt>{destinationLegActive ? 'Arrival at destination estimate' : 'Driver ETA to pickup'}</dt>
                <dd>
                  <span className="eta-pill">
                    <span className="eta-pill__live" />
                    ~{etaMinutes} min
                    {etaUpdatedAt && (
                      <span className="eta-pill__sub">· {formatRelativeTime(etaUpdatedAt)}</span>
                    )}
                  </span>
                </dd>
              </div>
            )}
          </dl>
          {isActive && (
            <div className="tracking-map-section">
              <div className="tracking-map-header">
                <h4>
                  {destinationLegActive ? 'En route to destination' : 'Driver en route to pickup'}
                </h4>
                {!driverLatLng && (
                  <small style={{ color: 'var(--text-secondary)' }}>
                    Waiting for driver location…
                  </small>
                )}
                {driverLatLng ? <small style={{ color: driverPosition?.trackingHealth === 'lost' ? '#dc2626' : 'var(--text-secondary)' }}>Last ping {formatRelativeTime(driverPosition?.capturedAt || lastLocationAtRef.current)} · {(Number(driverPosition?.speed || 0) * 2.23694).toFixed(0)} mph · GPS ±{Math.round(Number(driverPosition?.accuracy || 0))} m{driverPosition?.trackingHealth === 'lost' ? ' · LOST SIGNAL' : ''}</small> : null}
              </div>
              <div className="tracking-map-wrapper">
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  style={{ height: '300px', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer url={tileUrl} attribution={tileAttr} maxZoom={20} />
                  {fitPositions.length >= 2 && <MapAutoFit positions={fitPositions} />}
                  {routeCoords && routeCoords.length >= 2 && (
                    <Polyline
                      positions={routeCoords}
                      pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.85 }}
                    />
                  )}
                  {driverTrail.length >= 2 ? <Polyline positions={driverTrail} pathOptions={{ color: '#0ea5e9', weight: 3, opacity: 0.55, dashArray: '6 6' }} /> : null}
                  {driverLatLng && (
                    <SmoothBookingMarker
                      position={driverLatLng}
                      icon={makeDriverIcon(driverPosition?.heading)}
                    />
                  )}
                  {pickupLatLng && (
                    <Marker position={pickupLatLng} icon={pickupIcon} />
                  )}
                  {dropoffLatLng && (
                    <Marker position={dropoffLatLng} icon={dropoffIcon} />
                  )}
                </MapContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <AppLayout
      title={`Booking ${booking?.bookingId || id?.slice(-6) || ''}`.trim()}
      subtitle="Manage assignment, lifecycle and trip logistics from a single workspace."
      actions={actions}
    >
      <div className="surface">
        {loading ? (
          <div className="skeleton" style={{ height: '420px' }} />
        ) : (
          <>
            {message && <div className="feedback success">{message}</div>}
            {error && <div className="feedback error">{error}</div>}
            {booking?.needs_reassignment && (
              <div className="feedback warning">
                Automatic dispatch did not find an available driver. Assign manually or retry when more drivers are online.
              </div>
            )}
            {renderSummary()}

            <div className="form-grid two-column">
              <section className="panel">
                <form onSubmit={submitUpdate}>
                  <div className="panel-header">
                    <h3>Trip details</h3>
                  </div>
                  <div className="panel-body">
                    <div className="form-grid">
                      <div>
                        <label htmlFor="customerName">Customer name</label>
                        <input
                          id="customerName"
                          name="customerName"
                          type="text"
                          value={form.customerName}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="phoneNumber">Phone number</label>
                        <input
                          id="phoneNumber"
                          name="phoneNumber"
                          type="tel"
                          value={form.phoneNumber}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="pickupAddress">Pickup address</label>
                        <input
                          id="pickupAddress"
                          name="pickupAddress"
                          type="text"
                          value={form.pickupAddress}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="dropoffAddress">Drop-off address</label>
                        <input
                          id="dropoffAddress"
                          name="dropoffAddress"
                          type="text"
                          value={form.dropoffAddress}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="pickupTime">Pickup time</label>
                        <input
                          id="pickupTime"
                          name="pickupTime"
                          type="datetime-local"
                          value={form.pickupTime}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="fare">Quoted fare</label>
                        <input
                          id="fare"
                          name="fare"
                          type="number"
                          step="0.01"
                          value={form.fare}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="fareStrategy">Fare type</label>
                        <select
                          id="fareStrategy"
                          name="fareStrategy"
                          value={form.fareStrategy}
                          onChange={handleFormChange}
                        >
                          <option value="meter">Meter fare</option>
                          <option value="flat">Fixed fare</option>
                        </select>
                      </div>
                      {form.fareStrategy === 'flat' && (
                        <>
                          <div>
                            <label htmlFor="flatRateAmount">Fixed fare amount</label>
                            <input
                              id="flatRateAmount"
                              name="flatRateAmount"
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.flatRateAmount}
                              onChange={handleFormChange}
                            />
                          </div>
                          <div>
                            <label htmlFor="flatRateName">Fixed fare label</label>
                            <input
                              id="flatRateName"
                              name="flatRateName"
                              type="text"
                              value={form.flatRateName}
                              onChange={handleFormChange}
                              placeholder="Account fixed fare"
                            />
                          </div>
                        </>
                      )}
                      <div className="full-width">
                        <label htmlFor="notes">Notes</label>
                        <textarea
                          id="notes"
                          name="notes"
                          rows={3}
                          value={form.notes}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="panel-footer">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      Save changes
                    </button>
                  </div>
                </form>
              </section>

              <section className="panel">
                <form onSubmit={submitAssignment}>
                  <div className="panel-header">
                    <h3>Assignment</h3>
                  </div>
                  <div className="panel-body">
                    <div className="form-grid">
                      <div className="full-width">
                        <label htmlFor="assignmentMode">Mode</label>
                        <select
                          id="assignmentMode"
                          value={assignmentMode}
                          onChange={(e) => setAssignmentMode(e.target.value)}
                        >
                          <option value="manual">Manual</option>
                          <option value="auto">Auto assign</option>
                        </select>
                      </div>
                      {assignmentMode === 'manual' && (
                        <>
                          <div>
                            <label htmlFor="driverId">Driver</label>
                            <select
                              id="driverId"
                              name="driverId"
                              value={assignment.driverId}
                              onChange={handleAssignmentChange}
                            >
                              <option value="">Select driver…</option>
                              {driverOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {selectedActive && (
                            <div className="full-width" style={{ marginTop: '4px' }}>
                              <div
                                style={{
                                  border: '1px solid var(--surface-border)',
                                  borderRadius: '16px',
                                  padding: '16px',
                                  background: 'var(--surface-highlight)',
                                }}
                              >
                                <h4 style={{ margin: '0 0 12px' }}>Active driver snapshot</h4>
                                <dl className="meta-grid">
                                  <div>
                                    <dt>Cab</dt>
                                    <dd>{selectedActive.cabNumber || '—'}</dd>
                                  </div>
                                  <div>
                                    <dt>Plates</dt>
                                    <dd>{selectedActive.licPlates || '—'}</dd>
                                  </div>
                                  <div>
                                    <dt>Vehicle</dt>
                                    <dd>
                                      {[selectedActive.make, selectedActive.model, selectedActive.color]
                                        .filter(Boolean)
                                        .join(' ') || '—'}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>Status</dt>
                                    <dd>
                                      {[selectedActive.status, selectedActive.availability]
                                        .filter(Boolean)
                                        .join(' · ') || '—'}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>Location</dt>
                                    <dd>
                                      {(() => {
                                        const coords = Array.isArray(selectedActive?.currentLocation?.coordinates)
                                          ? selectedActive.currentLocation.coordinates
                                          : [];
                                        const [lng, lat] = coords;
                                        const hasCoords =
                                          Number.isFinite(lat) && Number.isFinite(lng);
                                        const formatted = hasCoords
                                          ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
                                          : '—';
                                        const updated = selectedActive?.currentLocation?.updatedAt
                                          ? new Date(selectedActive.currentLocation.updatedAt).toLocaleString()
                                          : null;
                                        return updated ? `${formatted} (updated ${updated})` : formatted;
                                      })()}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>HOS (driving today)</dt>
                                    <dd>
                                      {Number.isFinite(selectedActive?.hoursOfService?.drivingMinutesToday)
                                        ? `${selectedActive.hoursOfService.drivingMinutesToday} min`
                                        : '—'}
                                    </dd>
                                  </div>
                                </dl>
                              </div>
                            </div>
                          )}
                          <div>
                            <label htmlFor="cabNumber">Cab number</label>
                            <select
                              id="cabNumber"
                              name="cabNumber"
                              value={assignment.cabNumber}
                              onChange={handleAssignmentChange}
                            >
                              <option value="">Select cab…</option>
                              {vehicleOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="panel-footer">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {assignmentMode === 'auto' ? 'Auto assign' : 'Assign booking'}
                    </button>
                  </div>
                </form>

                <form onSubmit={submitStatusChange} style={{ marginTop: '24px' }}>
                  <div className="panel-header">
                    <h3>Status controls</h3>
                  </div>
                  <div className="panel-body">
                    <div className="form-grid">
                      <div>
                        <label htmlFor="toStatus">New status</label>
                        <select
                          id="toStatus"
                          name="toStatus"
                          value={statusForm.toStatus}
                          onChange={handleStatusChange}
                        >
                          {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="reason">Reason</label>
                        <input
                          id="reason"
                          name="reason"
                          type="text"
                          value={statusForm.reason}
                          onChange={handleStatusChange}
                        />
                      </div>
                      <div>
                        <label htmlFor="fee">Additional fee</label>
                        <input
                          id="fee"
                          name="fee"
                          type="number"
                          step="0.01"
                          value={statusForm.fee}
                          onChange={handleStatusChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="panel-footer">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving.' : 'Update booking status'}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default BookingDetail;
